import React, { useEffect, useState } from "react";
import { getTeam, inviteTeammate, revokeInvite, changeRole, removeTeammate, renameOrg, formatApiError } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, UserPlus, Loader2, Trash2, Copy, Building2, ShieldCheck, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const TeamCard = () => {
  const { refreshUsers } = useApp();
  const { refresh } = useAuth();
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("reviewer");
  const [busy, setBusy] = useState(false);
  const [orgName, setOrgName] = useState("");

  const load = () => getTeam().then((t) => { setTeam(t); setOrgName(t.org.name || ""); });
  useEffect(() => { load(); if (window.location.hash === "#team") setTimeout(() => document.getElementById("team")?.scrollIntoView({ behavior: "smooth" }), 200); }, []);

  const act = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); toast.success(typeof ok === "function" ? ok(r) : ok); await load(); refreshUsers(); return r; }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const invite = async (e) => {
    e.preventDefault();
    const r = await act(() => inviteTeammate(email, role), (r) => r.email_status === "sent" ? `Invitation emailed to ${r.email}` : "Invitation created — copy the link below to share it");
    if (r) setEmail("");
  };

  const copy = (url) => navigator.clipboard?.writeText(url).then(() => toast.success("Invite link copied"));

  if (!team) return null;
  const inviteUrl = (i) => `${window.location.origin}/register?invite=${i.token || ""}`;

  return (
    <div id="team" className="rounded-2xl border border-border bg-card p-6" data-testid="team-card">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-primary/10 p-2.5"><Users className="h-5 w-5 text-primary" /></div>
        <div className="flex-1">
          <h2 className="text-base md:text-lg font-semibold">Team &amp; workspace</h2>
          <p className="text-sm text-muted-foreground">Invite reviewers by email. Admins manage rules, accounts, Gmail and billing; reviewers work the queue.</p>
        </div>
        <span className="rounded-md bg-muted px-2 py-1 text-xs font-mono" data-testid="team-plan">{team.org.plan}{team.org.trial_ends_at ? ` · trial ends ${new Date(team.org.trial_ends_at).toLocaleDateString()}` : ""}</span>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <Input data-testid="org-name-input" value={orgName} onChange={(e) => setOrgName(e.target.value)} className="max-w-xs h-9" />
        <Button data-testid="org-name-save" size="sm" variant="outline" disabled={busy || !orgName.trim() || orgName === team.org.name}
                onClick={() => act(() => renameOrg(orgName).then(refresh), "Workspace renamed")}>Rename</Button>
      </div>

      <ul className="mt-5 divide-y divide-border rounded-xl border border-border" data-testid="team-users">
        {team.users.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`team-user-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{u.name} {u.id === team.me.id && <span className="text-xs text-muted-foreground">(you)</span>}</div>
              <div className="text-xs text-muted-foreground truncate">{u.email}{u.last_login_at ? ` · last seen ${new Date(u.last_login_at).toLocaleDateString()}` : ""}</div>
            </div>
            <select data-testid={`team-role-${u.id}`} value={u.role} disabled={busy || u.id === team.me.id}
                    onChange={(e) => act(() => changeRole(u.id, e.target.value), `${u.name} is now ${e.target.value}`)}
                    className="h-8 rounded-md border border-input bg-background px-2 text-xs capitalize">
              <option value="admin">admin</option><option value="reviewer">reviewer</option>
            </select>
            {u.id !== team.me.id && (
              <Button data-testid={`team-remove-${u.id}`} size="sm" variant="ghost" disabled={busy} onClick={() => act(() => removeTeammate(u.id), `${u.name} removed`)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
            )}
          </li>
        ))}
      </ul>

      <form onSubmit={invite} className="mt-4 flex flex-wrap items-center gap-2" data-testid="invite-form">
        <UserPlus className="h-4 w-4 text-muted-foreground" />
        <Input data-testid="invite-email" type="email" required placeholder="colleague@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 min-w-[200px] h-9" />
        <select data-testid="invite-role" value={role} onChange={(e) => setRole(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
          <option value="reviewer">Reviewer</option><option value="admin">Admin</option>
        </select>
        <Button data-testid="invite-submit" type="submit" size="sm" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1.5" /> Invite</>}</Button>
      </form>

      {team.invites.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="pending-invites">
          {team.invites.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs" data-testid={`invite-row-${i.id}`}>
              <ShieldCheck className="h-3.5 w-3.5 text-amber-500" />
              <span className="font-medium">{i.email}</span>
              <span className="text-muted-foreground capitalize">· {i.role} · {i.email_status === "sent" ? "emailed" : "email not delivered — share the link"} · expires {new Date(i.expires_at).toLocaleDateString()}</span>
              <span className="ml-auto flex items-center gap-1">
                {i.invite_url && <Button size="sm" variant="ghost" className="h-7" onClick={() => copy(i.invite_url)}><Copy className="h-3 w-3 mr-1" /> Copy link</Button>}
                <Button data-testid={`invite-revoke-${i.id}`} size="sm" variant="ghost" className="h-7 text-red-500" disabled={busy} onClick={() => act(() => revokeInvite(i.id), "Invitation revoked")}>Revoke</Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
